/**
 * SSO client cho ERP Accounting.
 *
 * Handle 2 entry points:
 *   1. Từ Launcher (my-works) — session cookie đã có từ SSO trước đó → skip login
 *   2. Direct URL — chưa có session → redirect central SSO → callback → set session
 */

const ERP_BASE_URL = (import.meta.env.VITE_ERP_BASE_URL as string) || 'https://erplite.hitek.com.vn';

export type ErpRole = 'KTT' | 'NV_KT' | 'CEO' | 'NV';

export interface CurrentUser {
  id: string;
  erpUserId?: string;
  name: string;
  email: string;
  role: ErpRole;
  roleLabel?: string;
  departmentId?: string;
  departmentName?: string;
  avatarUrl?: string;
  /** Danh sách page keys user được xem — filter sidebar theo đây. */
  pages?: string[];
  perms?: string[];
}

const SESSION_KEY = 'erp_ac_session';
const DEV_USER_KEY = 'erp_ac_dev_user';

interface SessionCookie {
  token: string;
  expiresAt: number;
  refreshToken?: string;
}

function readSession(): SessionCookie | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as SessionCookie;
    if (Date.now() >= s.expiresAt) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function writeSession(session: SessionCookie): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getSessionToken(): string | null {
  return readSession()?.token ?? null;
}

/**
 * Đổi refreshToken lấy accessToken mới. Trả true nếu refresh thành công.
 * Dùng khi /api/* trả 401 (token hết hạn) — thử refresh trước khi đá user về /login.
 * Deduplicate: nếu nhiều request cùng lúc phát hiện 401, chỉ 1 request thực sự gọi refresh.
 */
let refreshInFlight: Promise<boolean> | null = null;
export async function tryRefreshToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const session = readSession();
      const refreshToken = session?.refreshToken;
      if (!refreshToken) return false;
      const res = await fetch('/api/auth/sso/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken?: string; expiresIn?: number; refreshToken?: string };
      if (!data.accessToken) return false;
      writeSession({
        token: data.accessToken,
        expiresAt: Date.now() + (data.expiresIn ?? 900) * 1000,
        refreshToken: data.refreshToken ?? refreshToken,
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(DEV_USER_KEY);
}

/**
 * Fetch current user info từ ERP-AC backend.
 * Backend verify token đăng nhập qua JwtAuthGuard.
 *
 * Dev mode: nếu có DEV_USER_KEY trong localStorage → skip backend, dùng mock user.
 */
export async function fetchMe(): Promise<CurrentUser | null> {
  // Dev bypass — không cần backend/DB
  if (import.meta.env.DEV) {
    try {
      const raw = localStorage.getItem(DEV_USER_KEY);
      if (raw) return JSON.parse(raw) as CurrentUser;
    } catch {
      /* fall through */
    }
  }

  const token = getSessionToken();
  if (!token) return null;

  try {
    let res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      // Access token hết hạn → thử refresh trước khi bỏ session
      const refreshed = await tryRefreshToken();
      if (!refreshed) {
        clearSession();
        return null;
      }
      const newToken = getSessionToken();
      if (!newToken) {
        clearSession();
        return null;
      }
      res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      if (res.status === 401) {
        clearSession();
        return null;
      }
    }
    if (!res.ok) throw new Error(`Auth /me fail: ${res.status}`);
    return (await res.json()) as CurrentUser;
  } catch (err) {
    console.error('[auth] fetchMe error', err);
    return null;
  }
}

/**
 * DEV ONLY — mock login mà không cần ERP Base / DB.
 * Dùng để test UI (sidebar filter, role badge, permission matrix).
 * KHÔNG bao giờ dùng trong production.
 */
export function devLoginAs(role: ErpRole): CurrentUser {
  if (!import.meta.env.DEV) {
    throw new Error('devLoginAs chỉ dùng trong dev mode.');
  }

  const mockUsers: Record<ErpRole, CurrentUser> = {
    KTT: {
      id: 'dev-ktt',
      erpUserId: 'dev-ktt',
      name: 'Nguyễn Thị Hà',
      email: 'hant@hitek.vn',
      role: 'KTT',
      roleLabel: 'Kế toán trưởng',
      departmentId: 'dept-ktt',
      departmentName: 'Kế toán',
      pages: ['dashboard', 'approvals', 'cashledger', 'ledger', 'payments', 'payroll', 'contracts', 'congnoda', 'assets', 'hr', 'congtacqt', 'vaynh', 'reports', 'hopnhat', 'settings', 'guide'],
      perms: ['Duyệt đề nghị chi + bảng lương', 'Quản lý COA / Số dư / Kỳ kế toán', 'Duyệt bút toán · Khoá kỳ'],
    },
    NV_KT: {
      id: 'dev-nvkt',
      erpUserId: 'dev-nvkt',
      name: 'Lê Huỳnh Diệu',
      email: 'dieulh@hitek.vn',
      role: 'NV_KT',
      roleLabel: 'NV Kế toán',
      departmentId: 'dept-ktt',
      departmentName: 'Kế toán',
      pages: ['dashboard', 'cashledger', 'ledger', 'payments', 'payroll', 'mypayslips', 'contracts', 'congnoda', 'assets', 'congtacqt', 'vaynh', 'reports', 'guide'],
      perms: ['Lập phiếu chi + bảng lương', 'Nhập chứng từ + hạch toán (nháp)', 'Quản lý TSCĐ + HĐ khách hàng'],
    },
    CEO: {
      id: 'dev-ceo',
      erpUserId: 'dev-ceo',
      name: 'Phạm Minh Đức',
      email: 'ducpm@hitek.vn',
      role: 'CEO',
      roleLabel: 'CEO',
      departmentId: 'dept-bgd',
      departmentName: 'Ban Giám đốc',
      pages: ['dashboard', 'approvals', 'payroll', 'contracts', 'reports', 'hopnhat', 'guide'],
      perms: ['Ký duyệt cuối bảng lương + phiếu chi lớn', 'Xem hợp đồng khách hàng lớn', 'Xem báo cáo tài chính (P&L · BS · CF)'],
    },
    NV: {
      id: 'dev-nv',
      erpUserId: 'dev-nv',
      name: 'Trần Thị B',
      email: 'btt@hitek.vn',
      role: 'NV',
      roleLabel: 'Nhân viên',
      departmentId: 'dept-marketing',
      departmentName: 'Marketing',
      pages: ['payments', 'mypayslips', 'congtacqt', 'guide'],
      perms: ['Lập đề nghị chi', 'Xem phiếu lương của tôi', 'Không xem được sổ cái / báo cáo'],
    },
  };

  const user = mockUsers[role];
  localStorage.setItem(DEV_USER_KEY, JSON.stringify(user));
  // fake session token để apiFetch hoạt động (dù backend chưa verify)
  writeSession({
    token: `dev-token-${role}-${Date.now()}`,
    expiresAt: Date.now() + 24 * 3600 * 1000,
  });
  return user;
}

/**
 * Base SSO không phải OAuth — Launcher khởi tạo. User phải đăng nhập ở
 * erplite.hitek.com.vn rồi click icon "Kế toán" trong Launcher. Base sinh code
 * TTL 60s và redirect {redirectUrl}?code=&state=&platform={clientId}.
 *
 * Không có endpoint /sso/authorize để app khởi tạo. Nếu user truy cập thẳng
 * ac.erplite.hitek.com.vn mà chưa có session thì đẩy về Launcher.
 */
export function redirectToLauncher(): void {
  window.location.assign(ERP_BASE_URL);
}

/**
 * Đổi code (Base cấp qua redirect từ Launcher) lấy token user.
 * State do Base sinh — mình chỉ pass-through, không verify vì mình không
 * khởi tạo flow. Backend giữ x-platform-id/secret.
 */
export async function exchangeSsoCode(code: string, _state: string): Promise<CurrentUser> {
  const res = await fetch('/api/auth/sso/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(`SSO exchange fail: ${res.status}`);

  const data = (await res.json()) as {
    accessToken?: string;
    expiresIn?: number;
    refreshToken?: string;
    user?: CurrentUser;
  };

  if (!data.accessToken) throw new Error('Base tra ve khong co accessToken');
  writeSession({
    token: data.accessToken,
    expiresAt: Date.now() + (data.expiresIn ?? 900) * 1000,
    refreshToken: data.refreshToken,
  });

  // Gọi /api/auth/me để lấy role/pages/perms accounting derive từ department.
  const me = await fetchMe();
  if (me) return me;

  // Fallback: nếu /me fail (VD Employee chưa sync, JWT verify lỗi tạm thời),
  // dùng user object Base trả về + derive role/pages client-side.
  // Không idea, nhưng còn hơn kẹt user ở màn "Khong lay duoc thong tin".
  if (data.user) {
    console.warn('[auth] /me returned null, falling back to Base user + client-derive role');
    return enrichUserFromBase(data.user);
  }

  throw new Error('Khong lay duoc thong tin nguoi dung sau khi exchange');
}

/**
 * Derive role/pages/perms client-side khi /me fail. Logic phải khớp với
 * BE (roles.config.ts + derive-role.ts).
 */
function enrichUserFromBase(base: CurrentUser & { managedDepartmentIds?: string[] }): CurrentUser {
  const dept = base.departmentId;
  const managed = base.managedDepartmentIds ?? [];
  let role: ErpRole;
  if (dept === 'bod' || managed.includes('bod')) role = 'CEO';
  else if (dept === 'finance') role = managed.includes('finance') ? 'KTT' : 'NV_KT';
  else role = 'NV';

  const ROLE_PAGES_FE: Record<ErpRole, string[]> = {
    KTT: ['dashboard', 'approvals', 'cashledger', 'ledger', 'payments', 'payroll', 'contracts', 'congnoda', 'assets', 'hr', 'congtacqt', 'vaynh', 'reports', 'hopnhat', 'settings', 'guide'],
    NV_KT: ['dashboard', 'cashledger', 'ledger', 'payments', 'payroll', 'mypayslips', 'contracts', 'congnoda', 'assets', 'congtacqt', 'vaynh', 'reports', 'guide'],
    NV: ['payments', 'mypayslips', 'congtacqt', 'guide'],
    CEO: ['dashboard', 'approvals', 'payroll', 'contracts', 'reports', 'hopnhat', 'guide'],
  };
  const ROLE_LABEL_FE: Record<ErpRole, string> = {
    KTT: 'Kế toán trưởng', NV_KT: 'NV Kế toán', NV: 'Nhân viên', CEO: 'CEO',
  };

  return {
    ...base,
    role,
    roleLabel: ROLE_LABEL_FE[role],
    pages: ROLE_PAGES_FE[role],
    perms: base.perms ?? [],
  };
}

/**
 * Logout — revoke ở Base + clear session local.
 */
export async function logout(): Promise<void> {
  const token = getSessionToken();
  clearSession();
  if (token) {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: token }),
      });
    } catch {
      /* best-effort */
    }
  }
}
